// Canonical provider + model identifiers. Wire-format: strings above are what
// land on the network; enums here are the closed set the platform accepts.
// Gemini is the day-one provider for Phase 1 — OpenAI + Anthropic follow.
import { Type } from '@sinclair/typebox';
export const PROVIDERS = ['gemini', 'openai', 'anthropic'];
// No `$id` — Fastify/ajv treats every embedding of this schema as a separate
// inline definition, which is what we want. Setting an $id used to register
// a single shared ref, but it's reused unchanged across many route schemas
// (agents, chat, conversations, usage). Multiple addSchema calls with the
// same $id raise FST_ERR_SCH_VALIDATION_BUILD at boot.
export const ProviderSchema = Type.Union(PROVIDERS.map((p) => Type.Literal(p)));
// FinishReason is normalized across providers. Each adapter maps its native
// terminal signal onto one of these. `abort_*` are platform-generated, not
// provider-reported — budget/step/wall-clock ceilings.
export const FINISH_REASONS = [
    'stop',
    'length',
    'tool_calls',
    'content_filter',
    'abort_ceiling',
    'abort_max_steps',
    'abort_wall_clock',
    'error',
];
// Same rationale as ProviderSchema above — keep this anonymous so each
// route inlines it independently.
export const FinishReasonSchema = Type.Union(FINISH_REASONS.map((r) => Type.Literal(r)));
// Known model IDs, per provider. Kept as a closed list so billing, routing, and
// telemetry attrs can branch safely. New models land via PR, not config.
//
// Per-plan availability is gated by `plan_limits.allowed_models` — the entries
// here are the *type-system universe*, not the *runtime menu*. A model can
// exist here and still be unreachable on every current plan (e.g.
// claude-sonnet-4-6 is reserved for a future Pro+ add-on; no plan ships it
// today). See `revisao-v2.md` §6 #1 for the rationale.
//
// claude-opus-4-7 was removed in 2026-05 — no combination of our caps and the
// $15/$75 per-1M tokens price yields positive margin (see revisao-v2.md §4).
export const MODELS_BY_PROVIDER = {
    gemini: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'],
    openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1-mini'],
    anthropic: ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001'],
};
export const ALL_MODELS = Object.values(MODELS_BY_PROVIDER).flat();
export function isKnownModel(value) {
    return ALL_MODELS.includes(value);
}
export function providerForModel(model) {
    for (const p of PROVIDERS) {
        if (MODELS_BY_PROVIDER[p].includes(model))
            return p;
    }
    // unreachable by type — ModelId is a union over MODELS_BY_PROVIDER values.
    throw new Error(`unknown model: ${model}`);
}
// Blended-cost rank (30% input + 70% output per 1M tokens) at 2026-05.
// Lower = cheaper. Single source of truth so the dashboard can sort
// model dropdowns by price without depending on @cortex/providers (which
// is API-side only). The CI sentinel in packages/providers/tests/pricing.test.ts
// keeps these numbers in sync with the authoritative pricing.ts table —
// rebuild this map whenever pricing.ts changes.
export const MODEL_PRICE_RANK = {
    'gemini-2.5-flash-lite': 0.31,
    'gpt-4o-mini': 0.46,
    'gpt-4.1-mini': 1.24,
    'gemini-2.5-flash': 1.84,
    'claude-haiku-4-5-20251001': 3.8,
    'gemini-2.5-pro': 7.38,
    'gpt-4o': 7.75,
    'claude-sonnet-4-6': 11.4,
};
// Pick the cheapest model that the plan allows. Returns null when allowed
// is empty (mis-seeded plan_limits row) or when none of its entries match
// a known model — caller decides what to render in that degenerate case.
// Unknown ids in `allowed` are skipped silently so a future model rolled
// out via plan_limits ahead of MODELS_BY_PROVIDER can't crash the dashboard.
export function cheapestAllowedModel(allowed) {
    let best = null;
    let bestRank = Infinity;
    for (const candidate of allowed) {
        if (!isKnownModel(candidate))
            continue;
        const rank = MODEL_PRICE_RANK[candidate];
        if (rank < bestRank) {
            best = candidate;
            bestRank = rank;
        }
    }
    return best;
}
// Provider that owns the cheapest allowed model — used by the dashboard's
// cascading provider/model select to land on the cheapest pair on first
// render. Returns null when no allowed model is known.
export function cheapestProviderFor(allowed) {
    const m = cheapestAllowedModel(allowed);
    return m ? providerForModel(m) : null;
}
//# sourceMappingURL=providers.js.map